import { Injectable, signal } from '@angular/core';

/**
 * Tipos de notificación. `cargando` es el estado para procesos largos: no se
 * cierra solo, no lleva barra de progreso, y muta a `ok` o `error` en la MISMA
 * notificación cuando el proceso termina.
 */
export type NotifyKind = 'ok' | 'error' | 'warn' | 'info' | 'cargando';

/** Enlace opcional al pie del mensaje. */
export interface NotifyAction {
  label: string;
  run: () => void;
}

export interface NotifyOptions {
  /** Por defecto `ok`. */
  tipo?: NotifyKind;
  titulo: string;
  desc?: string;
  accion?: NotifyAction;
  /** Milisegundos. `0` = no se cierra sola. Si no se pasa, se usa el default del tipo. */
  duracion?: number;
}

/** Referencia devuelta por `show()`: permite cerrar o mutar la notificación. */
export interface NotifyRef {
  readonly id: number;
  close(): void;
  /** Muta la notificación en su lugar. Se usa para resolver un `cargando`. */
  update(patch: Partial<NotifyOptions>): void;
}

/** Estado interno de cada notificación viva. */
export interface NotifyItem {
  id: number;
  tipo: NotifyKind;
  titulo: string;
  desc?: string;
  accion?: NotifyAction;
  /** Duración total con la que se calcula la barra. `0` = sin cierre automático. */
  duracion: number;
  /** Milisegundos restantes. Sólo baja cuando `paused` es false. */
  restante: number;
  paused: boolean;
  /** Marca de salida: la usa el host para animar antes de desmontar. */
  saliendo: boolean;
}

/** §4: 5s por defecto, 8s cuando la notificación ofrece una acción. */
const DURACION_SIMPLE = 5000;
const DURACION_CON_ACCION = 8000;

/**
 * §4: al sacar el mouse de encima, si quedaba menos que esto, se le devuelve
 * este tiempo. Así el operador nunca pierde el mensaje por haberlo mirado.
 */
const RESTANTE_MINIMO_AL_SALIR = 1500;

/** §1 y §9.6: nunca más de 3 a la vista; la cuarta cierra la más vieja. */
const MAX_VISIBLES = 3;

/**
 * Servicio global de notificaciones. Reemplaza a ngx-toastr en todo el
 * backoffice: es el único camino para mostrar una confirmación, un error o el
 * progreso de un proceso largo.
 *
 * Expone un solo método (`show`). El tipo se nombra en el call site, así que
 * leer la llamada alcanza para saber qué va a ver el usuario.
 *
 * El servicio sólo mantiene la lista; el descuento del tiempo y las animaciones
 * viven en `NotifyHostComponent`, que es quien tiene un frame de referencia para
 * pausar con el mouse.
 */
@Injectable({ providedIn: 'root' })
export class NotifyService {
  private seq = 0;

  /** Lista viva, en orden de llegada. La más nueva es la última (§4: abajo). */
  readonly items = signal<NotifyItem[]>([]);

  show(opts: NotifyOptions): NotifyRef {
    const id = ++this.seq;
    const tipo = opts.tipo ?? 'ok';
    const duracion = this.resolverDuracion(tipo, opts);

    const item: NotifyItem = {
      id,
      tipo,
      titulo: opts.titulo,
      desc: opts.desc,
      accion: opts.accion,
      duracion,
      restante: duracion,
      paused: false,
      saliendo: false,
    };

    this.items.update((list) => {
      const next = [...list, item];
      // Al pasar el máximo, sale la más antigua que no esté ya saliendo.
      const vivas = next.filter((i) => !i.saliendo);
      if (vivas.length > MAX_VISIBLES) {
        const sobra = vivas.slice(0, vivas.length - MAX_VISIBLES);
        return next.map((i) => (sobra.includes(i) ? { ...i, saliendo: true } : i));
      }
      return next;
    });

    return {
      id,
      close: () => this.dismiss(id),
      update: (patch) => this.patch(id, patch),
    };
  }

  /** Marca la notificación para salir. El host la desmonta al terminar la animación. */
  dismiss(id: number): void {
    this.items.update((list) =>
      list.map((i) => (i.id === id ? { ...i, saliendo: true } : i)),
    );
  }

  /** Quita la notificación de la lista. Lo llama el host cuando terminó de animar. */
  remove(id: number): void {
    this.items.update((list) => list.filter((i) => i.id !== id));
  }

  /**
   * Muta una notificación en su lugar. El caso de uso es resolver un `cargando`:
   * `ref.update({ tipo: 'ok', titulo: 'Exportación lista' })`. Al dejar de ser
   * `cargando` arranca el temporizador, porque recién ahí tiene sentido que se
   * cierre sola.
   */
  patch(id: number, patch: Partial<NotifyOptions>): void {
    this.items.update((list) =>
      list.map((i) => {
        if (i.id !== id) return i;

        const tipo = patch.tipo ?? i.tipo;
        const accion = 'accion' in patch ? patch.accion : i.accion;
        // Si el patch trae duración explícita se respeta. Si no, y el tipo dejó
        // de ser `cargando`, se aplica el default para que empiece a correr.
        const duracion =
          patch.duracion !== undefined
            ? patch.duracion
            : i.tipo === 'cargando' && tipo !== 'cargando'
              ? (accion ? DURACION_CON_ACCION : DURACION_SIMPLE)
              : i.duracion;

        return {
          ...i,
          tipo,
          titulo: patch.titulo ?? i.titulo,
          desc: 'desc' in patch ? patch.desc : i.desc,
          accion,
          duracion,
          // Reiniciar el reloj: la notificación acaba de cambiar de significado.
          restante: duracion,
          paused: false,
        };
      }),
    );
  }

  /** Pausa o reanuda el temporizador de una notificación (hover del mouse). */
  setPaused(id: number, paused: boolean): void {
    this.items.update((list) =>
      list.map((i) => {
        if (i.id !== id) return i;
        if (paused) return { ...i, paused: true };
        // §4: al salir, nunca menos de 1,5s para leer.
        const restante = i.duracion > 0
          ? Math.max(i.restante, RESTANTE_MINIMO_AL_SALIR)
          : i.restante;
        return { ...i, paused: false, restante };
      }),
    );
  }

  /** Descuenta `delta` ms a las que estén corriendo y cierra las que llegaron a 0. */
  tick(delta: number): void {
    const list = this.items();
    if (!list.some((i) => this.corriendo(i))) return;

    this.items.set(
      list.map((i) => {
        if (!this.corriendo(i)) return i;
        const restante = i.restante - delta;
        return restante <= 0
          ? { ...i, restante: 0, saliendo: true }
          : { ...i, restante };
      }),
    );
  }

  /** Cierra todas. Se usa al desloguear, para no arrastrar mensajes de otra sesión. */
  clear(): void {
    this.items.set([]);
  }

  private corriendo(i: NotifyItem): boolean {
    return !i.saliendo && !i.paused && i.duracion > 0 && i.tipo !== 'cargando';
  }

  private resolverDuracion(tipo: NotifyKind, opts: NotifyOptions): number {
    // Un proceso largo no se cierra solo: lo cierra su propia resolución.
    if (tipo === 'cargando') return 0;
    if (opts.duracion !== undefined) return opts.duracion;
    return opts.accion ? DURACION_CON_ACCION : DURACION_SIMPLE;
  }
}
